import React, { useState } from "react";
import { IntlProvider, useIntl } from "react-intl";
import {
  getLoginLocale,
  useLoginTypewriter,
  useLoginSession,
  TYPEWRITER_FIELDS,
  TYPEWRITER_CURSOR_ALL,
} from "./loginHooks";
import "./Login.scss";

const LOGO_SRC = `${process.env.PUBLIC_URL}/taeurope-logo-white.svg`;
const LOGIN_BG_SRC = `${process.env.PUBLIC_URL}/assets/images/ta-login-bg.jpg`;

const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

/** Declarative typewriter line — text + optional cursor from React state. */
const TypewriterLine = ({ text, showCursor, light, as: Tag = "p", className }) => {
  const lines = text.split("\n");
  const cursorClass = light ? "ta-login__cursor-light" : "ta-login__cursor-dark";

  return (
    <Tag className={className}>
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {index > 0 && <br />}
          {line}
        </React.Fragment>
      ))}
      {showCursor && <span className={cursorClass} aria-hidden="true" />}
    </Tag>
  );
};

const LoginScreen = ({ setLocale }) => {
  const intl = useIntl();
  const { copy, cursorKey } = useLoginTypewriter(setLocale);
  useLoginSession();

  const [ssoLoading, setSsoLoading] = useState(false);

  const handleSSO = async () => {
    if (ssoLoading) return;
    setSsoLoading(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/auth/set-redirect?app=guru`);
      window.location.href = `${process.env.REACT_APP_API_URL}/oauth2/authorization/microsoft`;
    } catch {
      setSsoLoading(false);
    }
  };

  return (
    <main className="ta-login">
      <section className="ta-login__left">
        <div
          className="ta-login__left-bg"
          style={{ backgroundImage: `url('${LOGIN_BG_SRC}')` }}
          aria-hidden="true"
        />
        <div className="ta-login__left-grad" aria-hidden="true" />
        <div className="ta-login__left-top">
          <img src={LOGO_SRC} alt="TA Europe" className="ta-login__brand-logo" />
        </div>
        <div className="ta-login__left-bot">
          {TYPEWRITER_FIELDS.filter((f) => f.light).map(({ key, light, tag, className }) => (
            <TypewriterLine
              key={key}
              as={tag}
              className={className}
              text={copy[key]}
              showCursor={cursorKey === key || cursorKey === TYPEWRITER_CURSOR_ALL}
              light={light}
            />
          ))}
        </div>
        <div className="ta-login__deco" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </section>

      <section className="ta-login__right">
        <div className="ta-login__inner">
          <div className="ta-login__intro">
            {TYPEWRITER_FIELDS.filter((f) => !f.light && f.tag !== "span").map(
              ({ key, light, tag, className }) => (
                <TypewriterLine
                  key={key}
                  as={tag}
                  className={className}
                  text={copy[key]}
                  showCursor={cursorKey === key || cursorKey === TYPEWRITER_CURSOR_ALL}
                  light={light}
                />
              )
            )}
          </div>

          <div className="ta-login__stack">
            <button type="button" className="ta-login__sso" onClick={handleSSO} disabled={ssoLoading}>
              <MicrosoftIcon />
              <TypewriterLine
                as="span"
                className="ta-login__sso-label"
                text={copy["login.sso"]}
                showCursor={cursorKey === "login.sso" || cursorKey === TYPEWRITER_CURSOR_ALL}
                light={false}
              />
            </button>
          </div>
        </div>

        <div className="ta-login__footer" aria-hidden="true">
          <span className="ta-login__footer-txt">{intl.formatMessage({ id: "login.footer" })}</span>
        </div>
      </section>
    </main>
  );
};

const Login = () => {
  const [locale, setLocale] = useState(() => getLoginLocale());

  return (
    <IntlProvider locale={locale.locale} messages={locale.messages}>
      <LoginScreen setLocale={setLocale} />
    </IntlProvider>
  );
};

export default Login;
